import { useEffect } from 'react';

const MOBILE_BREAKPOINT_QUERY = '(max-width: 600px)';
const STICKY_STYLE_ELEMENT_ID = 'mobile-sticky-columns-style';
const STICKY_COLS_ATTR = 'data-mobile-sticky-cols';
const STICKY_LEFT_VAR_PREFIX = '--mobile-sticky-left-';
const TABLE_WRAPPER_SELECTOR = '.ant-table-wrapper';
const HEADER_CELL_SELECTOR = '.ant-table-thead > tr > th';
const ID_COLUMN_PATTERN = /\bid\b/i;

type StickyRule = {
  prefix: string;
  titles: string[];
  excludePaths?: string[];
};

const MODULE_STICKY_TITLE_RULES: StickyRule[] = [
  { prefix: '/products', titles: [], excludePaths: ['/products/create'] },
  { prefix: '/inventories', titles: ['sku'] },
  { prefix: '/clients', titles: ['name'] },
  { prefix: '/suppliers', titles: ['company name'] },
  { prefix: '/users', titles: ['logo'] },
  { prefix: '/restaurant-sales', titles: ['order id'] },
];

const normalizeText = (value: string): string => value.replace(/\s+/g, ' ').trim().toLowerCase();

const isRouteMatch = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const getStickyTitlesForRoute = (pathname: string): string[] | null => {
  const matchedRule = MODULE_STICKY_TITLE_RULES.find((rule) => isRouteMatch(pathname, rule.prefix));
  if (!matchedRule) {
    return null;
  }

  const isExcludedPath = (matchedRule.excludePaths ?? []).some((excludePath) =>
    isRouteMatch(pathname, excludePath),
  );
  if (isExcludedPath) {
    return [];
  }

  return matchedRule.titles.map(normalizeText);
};

const shouldStickColumn = (headerTitle: string, stickyTitles: string[] | null): boolean => {
  if (stickyTitles) {
    return stickyTitles.some((stickyTitle) => headerTitle.includes(stickyTitle));
  }

  return ID_COLUMN_PATTERN.test(headerTitle);
};

const getStickyColumnIndexes = (
  headerCells: HTMLElement[],
  stickyTitles: string[] | null,
): number[] => {
  return headerCells.reduce<number[]>((indexes, cell, index) => {
    const headerTitle = normalizeText(cell.textContent || '');
    if (shouldStickColumn(headerTitle, stickyTitles)) {
      indexes.push(index);
    }

    return indexes;
  }, []);
};

const insertedIndexes = new Set<number>();

const getStyleElement = (): HTMLStyleElement => {
  let styleElement = document.getElementById(STICKY_STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = STICKY_STYLE_ELEMENT_ID;
    document.head.appendChild(styleElement);
  }
  return styleElement;
};

const buildStickyCss = (index: number): string => {
  const nthChild = index + 1;
  const base = `.ant-table-wrapper[${STICKY_COLS_ATTR}~="${index}"]`;
  const cells = `${base} .ant-table-thead > tr > th:nth-child(${nthChild}),\n${base} .ant-table-tbody > tr > td:nth-child(${nthChild})`;
  const header = `${base} .ant-table-thead > tr > th:nth-child(${nthChild})`;

  return `@media (max-width: 600px) {\n${cells} {\n  position: sticky !important;\n  left: var(${STICKY_LEFT_VAR_PREFIX}${index}, 0px) !important;\n  z-index: 20;\n  background: #ffffff;\n}\n${header} {\n  z-index: 100 !important;\n  position: sticky !important;\n  overflow: hidden;\n  background: #fafafa !important;\n}\n${header}::before {\n  content: '';\n  position: absolute;\n  inset: 0;\n  z-index: 0;\n  background: #fafafa;\n}\n${header} > * {\n  position: relative;\n  z-index: 1;\n}\n${base} .ant-table-tbody > tr.ant-table-row:hover > td:nth-child(${nthChild}) {\n  background: #f5f5f5;\n}\n${cells}::after {\n  content: '';\n  position: absolute;\n  top: 0;\n  right: -1px;\n  width: 8px;\n  height: 100%;\n  pointer-events: none;\n  box-shadow: inset -8px 0 8px -8px rgba(9, 37, 69, 0.2);\n}\n}\n`;
};

const ensureStickyStyles = (indexes: number[]): void => {
  const newIndexes = indexes.filter((index) => !insertedIndexes.has(index));
  if (!newIndexes.length) {
    return;
  }

  const styleElement = getStyleElement();
  styleElement.appendChild(document.createTextNode(newIndexes.map(buildStickyCss).join('')));
  newIndexes.forEach((index) => insertedIndexes.add(index));
};

const clearStickyStyles = (tableWrapper: HTMLElement): void => {
  const stickyCols = tableWrapper.getAttribute(STICKY_COLS_ATTR);
  if (stickyCols) {
    stickyCols.split(' ').forEach((index) => {
      const trimmed = index.trim();
      if (trimmed) {
        tableWrapper.style.removeProperty(`${STICKY_LEFT_VAR_PREFIX}${trimmed}`);
      }
    });
  }

  tableWrapper.removeAttribute(STICKY_COLS_ATTR);
};

const applyStickyStyles = (tableWrapper: HTMLElement, stickyTitles: string[] | null): void => {
  const headerCells = Array.from(tableWrapper.querySelectorAll<HTMLElement>(HEADER_CELL_SELECTOR));

  if (!headerCells.length) {
    return;
  }

  const stickyColumnIndexes = getStickyColumnIndexes(headerCells, stickyTitles);
  if (!stickyColumnIndexes.length) {
    return;
  }

  ensureStickyStyles(stickyColumnIndexes);

  let stickyLeftOffset = 0;
  const appliedIndexes: number[] = [];

  stickyColumnIndexes.forEach((columnIndex) => {
    const headerCell = headerCells[columnIndex];
    if (!headerCell) {
      return;
    }

    tableWrapper.style.setProperty(
      `${STICKY_LEFT_VAR_PREFIX}${columnIndex}`,
      `${stickyLeftOffset}px`,
    );
    appliedIndexes.push(columnIndex);

    stickyLeftOffset += headerCell.getBoundingClientRect().width || headerCell.offsetWidth || 0;
  });

  if (appliedIndexes.length) {
    tableWrapper.setAttribute(STICKY_COLS_ATTR, appliedIndexes.join(' '));
  }
};

const syncStickyColumns = (isMobile: boolean): void => {
  const tableWrappers = Array.from(document.querySelectorAll<HTMLElement>(TABLE_WRAPPER_SELECTOR));
  const pathname = window.location.pathname.toLowerCase();
  const stickyTitles = getStickyTitlesForRoute(pathname);
  const shouldApplySticky = isMobile && stickyTitles?.length !== 0;

  tableWrappers.forEach((tableWrapper) => {
    if (!shouldApplySticky) {
      clearStickyStyles(tableWrapper);
      return;
    }

    applyStickyStyles(tableWrapper, stickyTitles);
  });
};

export const useMobileStickyIdColumns = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQueryList = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    let rafId: number | null = null;
    let mutationObserver: MutationObserver | null = null;
    const observerTarget = document.body;
    const observerConfig: MutationObserverInit = {
      childList: true,
      subtree: true,
    };

    const scheduleSync = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => {
        syncStickyColumns(mediaQueryList.matches);
      });
    };

    mutationObserver = new MutationObserver(scheduleSync);

    mutationObserver.observe(observerTarget, observerConfig);

    const handleViewportChange = () => scheduleSync();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleViewportChange);
    } else {
      mediaQueryList.addListener(handleViewportChange);
    }

    scheduleSync();

    return () => {
      mutationObserver?.disconnect();

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      if (typeof mediaQueryList.removeEventListener === 'function') {
        mediaQueryList.removeEventListener('change', handleViewportChange);
      } else {
        mediaQueryList.removeListener(handleViewportChange);
      }

      syncStickyColumns(false);
    };
  }, []);
};
